import { useEffect, useMemo, useState } from 'react'
import EditableField from '../components/EditableField'
import useDealStore from '../store/dealStore'
import useOrganizationStore from '../store/organizationStore'
import { MIN_LENDER_MATCH_SCORE } from '../data/stateTransitionRules.js'
import { analyzeInvestmentBaseline } from '../services/investmentBaselineService.js'
import { getSourceSummary } from '../utils/documentSourceUtils.js'
import '../styles/module5.css'
import '../styles/extractionLog.css'

// Task 5: Admin-configurable score thresholds (could be moved to settings panel)
const DEAL_SCORE_MINIMUM = parseInt(localStorage.getItem('deal_score_minimum') || '60', 10)
const DEAL_SCORE_MAXIMUM = parseInt(localStorage.getItem('deal_score_maximum') || '95', 10)

const clamp = (value, min, max) => Math.min(max, Math.max(min, value))
const money = (value) => `$${Math.round(value || 0).toLocaleString()}`
const percent = (value) => `${((value || 0) * 100).toFixed(1)}%`
const numDisplay = (value) => Number(value || 0).toFixed(2)
const metricDisplay = (value, formatter, fallback = 'TBD') => (
  value === null || value === undefined || value === '' ? fallback : formatter(value)
)
const maxZero = (value) => Math.max(0, Number.isFinite(Number(value)) ? Number(value) : 0)
const offerFloorPct = 0.06
const offerCeilingPct = 0.04

// ── Score color helpers ───────────────────────────────────────────────────────
// 3 tiers: green (≥75 → Buy), orange (60–74 → Review), red (<60 → Pass)
function scoreColor(score) {
  if (score > 75) return '#16a34a'
  if (score >= 70) return '#d97706'
  return '#dc2626'
}

function scoreBg(score) {
  if (score > 75) return '#dcfce7'
  if (score >= 70) return '#fef3c7'
  return '#fee2e2'
}

function barColor(value) {
  if (value > 75) return 'linear-gradient(90deg, #16a34a, #4ade80)'
  if (value >= 70) return 'linear-gradient(90deg, #d97706, #fbbf24)'
  return 'linear-gradient(90deg, #dc2626, #f87171)'
}

function legacyScoreColor(score) {
  if (score >= 75) return '#16a34a'   // green — Buy
  if (score >= 60) return '#d97706'   // amber — Review
  return '#dc2626'                    // red — Pass
}

function legacyScoreBg(score) {
  if (score >= 75) return '#dcfce7'
  if (score >= 60) return '#fef3c7'
  return '#fee2e2'
}

function legacyBarColor(value) {
  if (value >= 75) return 'linear-gradient(90deg, #16a34a, #4ade80)'
  if (value >= 60) return 'linear-gradient(90deg, #d97706, #fbbf24)'
  return 'linear-gradient(90deg, #dc2626, #f87171)'
}

const escapeHtml = (value = '') => String(value)
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;')
  .replaceAll("'", '&#039;')

function calculateScore(dealState) {
  const rawDeal = dealState.rawDealData || {}
  const financials = dealState.extractedFinancials || {}
  const underwriting = dealState.underwritingMetrics || {}

  const price = Number(rawDeal.price) || 0
  const noi = maxZero(underwriting.noi || financials.noi)
  const capRate = maxZero(Number(underwriting.capRate?.entry) || (price ? noi / price : 0))
  const dscr = maxZero(underwriting.dscr?.current)
  const cashOnCash = maxZero(underwriting.cashOnCash?.year1)
  const irr = maxZero(underwriting.irr?.levered || underwriting.irr?.unlevered)
  const occupancy = Number(financials.revenue?.rentRoll?.occupancyRate || financials.marketIntelligenceDetails?.marketOccupancy) || 91
  const initialAIScore = Number(rawDeal.initialAIScore) || 76

  // Track whether key financial inputs actually exist (vs being absent/zero).
  // When data is missing we should not apply penalties — missing ≠ bad.
  const hasPrice       = price > 0
  const hasCapRate     = capRate > 0
  const hasDscr        = dscr > 0
  const hasCashOnCash  = cashOnCash > 0
  const hasIrr         = irr > 0

  const financialStrength = clamp(Math.round(
    (hasCapRate    ? (capRate    / 0.08) * 35 : 0) +
    (hasDscr       ? (dscr       / 1.6)  * 35 : 0) +
    (hasCashOnCash ? (cashOnCash / 0.12) * 30 : 0)
  ), 45, 100)

  const marketPosition = clamp(Math.round(initialAIScore * 0.7 + (occupancy / 100) * 30), 45, 100)

  const valueAdd = clamp(Math.round(
    (hasIrr        ? (irr        / 0.16) * 55 : 0) +
    (hasCashOnCash ? (cashOnCash / 0.1)  * 30 : 0) +
    (occupancy < 94 ? 12 : 4)
  ), 40, 100)

  // When a metric is present and bad → full penalty.
  // When a metric is absent (data not yet entered) → small uncertainty deduction
  // so missing data doesn't score as "perfect" OR as "terrible".
  const riskLevel = clamp(Math.round(
    100
    - (hasDscr    ? Math.max(0, 1.25 - dscr)   * 45 : 15)  // unknown DSCR: -15
    - (hasCapRate ? Math.max(0, 0.05 - capRate) * 500 : 10) // unknown cap:  -10
    - Math.max(0, 88 - occupancy) * 2
  ), 35, 100)

  const score = clamp(Math.round(
    financialStrength * 0.34 +
    marketPosition    * 0.24 +
    valueAdd          * 0.22 +
    riskLevel         * 0.20
  ), 1, 100)

  const verdict = score >= 85
    ? 'Strong Buy'
    : score >= 72
      ? 'Strong potential with some manageable risks.'
      : score >= 60
        ? 'Proceed carefully after resolving the main risk items.'
        : 'Pass unless pricing improves materially.'

  const verdictCode = score >= 85 ? 'STRONG_BUY' : score >= 72 ? 'BUY' : score >= 60 ? 'REVIEW' : 'PASS'

  const riskAdjustment = score >= 85 ? 0.98 : score >= 72 ? 0.94 : score >= 60 ? 0.88 : 0.8
  const noiValue = capRate ? noi / Math.max(capRate, 0.01) : price
  const recommended = Math.round(Math.min(price * riskAdjustment, noiValue * riskAdjustment))
  const minimum = Math.round(recommended * (1 - offerFloorPct))
  const maximum = Math.round(recommended * (1 + offerCeilingPct))

  const strengths = [
    capRate >= 0.06 ? `Attractive entry cap rate of ${percent(capRate)} supports strong yield.` : `Cap rate is supported by current NOI and still clears minimum screening.`,
    dscr >= 1.25 ? `DSCR of ${dscr.toFixed(2)}x is above common lender requirements.` : `Debt coverage is close enough to evaluate with adjusted loan terms.`,
    cashOnCash >= 0.06 ? `Year-one cash-on-cash return of ${percent(cashOnCash)} creates visible investor yield.` : `Cash flow remains positive after debt service.`,
    occupancy < 94 ? `Occupancy upside creates room for operational improvement.` : `Stabilized occupancy supports predictable income.`,
  ]

  const risks = [
    dscr < 1.35 ? `Debt coverage has limited cushion if rates or expenses rise.` : `Debt coverage should still be stress-tested before final offer.`,
    capRate < 0.055 ? `Entry cap rate is tight and depends on execution.` : `Pricing discipline is still needed to protect returns.`,
    occupancy < 90 ? `Occupancy below 90% may signal leasing or property-condition risk.` : `Validate tenant quality, rent roll accuracy, and renewal assumptions.`,
    `Insurance, taxes, and deferred maintenance should be confirmed during diligence.`,
  ]

  const missingInputs = [
    !hasPrice && 'asking price',
    !hasDscr  && 'debt-service coverage (run underwriting)',
    !hasIrr   && 'IRR / cash-on-cash (run underwriting)',
  ].filter(Boolean)

  const partialNote = missingInputs.length
    ? ` Score is indicative — missing: ${missingInputs.join(', ')}. Complete the underwriting engine for a full score.`
    : ''

  const explanation = `AI scoring combines underwriting results, market intelligence, and property-level assumptions. The deal scores ${score}/100 based on ${financialStrength >= 75 ? 'strong' : 'available'} financial metrics, market position, and risk profile.${partialNote}`
  const pricingFactors = [
    { label: 'Financial performance', value: financialStrength, note: financialStrength >= 75 ? 'supports tighter bid' : 'requires pricing cushion' },
    { label: 'Market confidence', value: marketPosition, note: marketPosition >= 75 ? 'market supports pursuit' : 'market needs verification' },
    { label: 'Execution risk', value: riskLevel, note: riskLevel >= 75 ? 'risk discount moderate' : 'risk discount widened' },
    { label: 'Value-add upside', value: valueAdd, note: valueAdd >= 75 ? 'upside protects basis' : 'upside not yet proven' },
  ]

  return {
    score,
    verdict,
    verdictCode,
    explanation,
    recommended,
    minimum,
    maximum,
    confidence: score >= 85 ? 'High' : score >= 70 ? 'Medium-High' : 'Medium',
    breakdown: {
      financialStrength,
      marketPosition,
      riskLevel,
      valueAdd,
    },
    pricingFactors,
    pricingLogic: {
      askingPrice: price,
      noi,
      capRate,
      noiValue,
      riskAdjustment,
      discountPct: price ? (1 - recommended / price) * 100 : 0,
      floorPct: offerFloorPct,
      ceilingPct: offerCeilingPct,
    },
    strengths: strengths.slice(0, 4),
    risks: risks.slice(0, 4),
  }
}

function InfoHint({ label, title, children }) {
  return (
    <span className="m5-info-hint">
      <button type="button" className="m5-info-btn" aria-label={label} title={label}>
        i
      </button>
      <span className="m5-info-popover" role="tooltip">
        <strong>{title}</strong>
        {children}
      </span>
    </span>
  )
}

function BaselineMetric({ label, value, title, children, estimated = false }) {
  return (
    <div>
      <span className="m5-baseline-label">
        {label}
        {estimated && <em>(est.)</em>}
        <InfoHint label={`What is ${label}?`} title={title}>
          {children}
        </InfoHint>
      </span>
      <strong>{value}</strong>
    </div>
  )
}

// ── Mortgage Calculator ──────────────────────────────────────────────────────
function MortgageCalculator({ offerPrice }) {
  const { dealState, updateDealState } = useDealStore()
  const savedCalc = dealState.mortgageCalc || {}
  const [ltv,          setLtv]          = useState(savedCalc.ltv ?? 70)
  const [interestRate, setInterestRate] = useState(savedCalc.interestRate ?? 6.5)
  const [termYears,    setTermYears]    = useState(savedCalc.termYears ?? 30)

  const clampInput = (value, min, max, decimals = 0) => {
    const parsed = Number(value)
    if (!Number.isFinite(parsed)) return min
    const factor = 10 ** decimals
    return clamp(Math.round(parsed * factor) / factor, min, max)
  }

  const formatInputValue = (value, decimals = 0) => (
    decimals > 0 ? Number(value || 0).toFixed(decimals) : String(Math.round(Number(value || 0)))
  )

  useEffect(() => {
    updateDealState({ mortgageCalc: { ltv, interestRate, termYears } })
  }, [ltv, interestRate, termYears]) // eslint-disable-line

  const loanAmount    = Math.round((offerPrice || 0) * ltv / 100)
  const downPayment   = (offerPrice || 0) - loanAmount
  const monthlyRate   = interestRate / 100 / 12
  const n             = termYears * 12
  const monthlyPayment = loanAmount > 0 && monthlyRate > 0 && n > 0
    ? loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, n)) / (Math.pow(1 + monthlyRate, n) - 1)
    : 0
  const totalPaid      = monthlyPayment * n
  const totalInterest  = totalPaid - loanAmount

  const row = (label, value, highlight = false) => (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.08)',
    }}>
      <span style={{ fontSize: 12, color: '#93c5fd' }}>{label}</span>
      <strong style={{ fontSize: 13, color: highlight ? '#34d399' : '#fff' }}>{value}</strong>
    </div>
  )

  return (
    <div style={{
      marginTop: 20,
      background: 'rgba(255,255,255,0.06)',
      border: '1px solid rgba(255,255,255,0.12)',
      borderRadius: 12,
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <span style={{ fontSize: 16 }}>🏦</span>
        <span style={{ fontWeight: 700, fontSize: 14, color: '#fff' }}>Mortgage Calculator</span>
      </div>

      <div style={{ padding: '16px' }}>
        {/* Inputs */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 14 }}>
          {[
            { label: 'Down Payment (LTV %)', val: ltv,          set: setLtv,          min: 10, max: 90,  step: 1,   suffix: '%',  decimals: 0 },
            { label: 'Interest Rate (%)',     val: interestRate, set: setInterestRate, min: 2,  max: 15,  step: 0.1, suffix: '%',  decimals: 1 },
            { label: 'Loan Term (years)',     val: termYears,    set: setTermYears,    min: 5,  max: 40,  step: 1,   suffix: 'yr', decimals: 0 },
          ].map(({ label, val, set, min, max, step, suffix, decimals }) => (
            <div key={label}>
              <div style={{ fontSize: 10, color: '#93c5fd', marginBottom: 4, fontWeight: 600 }}>{label}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="range" min={min} max={max} step={step} value={val}
                  onChange={e => set(clampInput(e.target.value, min, max, decimals))}
                  style={{ flex: 1, accentColor: '#34d399' }}
                />
                <input
                  type="number"
                  min={min}
                  max={max}
                  step={step}
                  value={formatInputValue(val, decimals)}
                  onChange={e => set(clampInput(e.target.value, min, max, decimals))}
                  aria-label={label}
                  style={{
                    width: 96,
                    minWidth: 96,
                    boxSizing: 'border-box',
                    padding: '8px 10px',
                    borderRadius: 6,
                    border: '1px solid rgba(147,197,253,0.45)',
                    background: '#fff',
                    color: '#0f172a',
                    fontSize: 14,
                    fontWeight: 800,
                    textAlign: 'right',
                  }}
                />
                <span style={{ fontSize: 12, fontWeight: 700, color: '#34d399', minWidth: suffix === 'yr' ? 20 : 14 }}>
                  {suffix}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Results */}
        <div>
          {row('Purchase Price',   money(offerPrice || 0))}
          {row('Down Payment',     money(downPayment))}
          {row('Loan Amount',      money(loanAmount))}
          {row('Monthly Payment',  money(Math.round(monthlyPayment)), true)}
          {row('Total Interest',   money(Math.round(totalInterest)))}
          {row('Total Cost',       money(Math.round(totalPaid + downPayment)))}
        </div>
      </div>
    </div>
  )
}

function ScoreGauge({ score }) {
  const boundedScore = clamp(score, 0, 100)
  const rotation = -90 + (boundedScore / 100) * 180
  const color = scoreColor(score)
  const bg = scoreBg(score)
  const scoreLabel = score > 75 ? 'Green Buy' : score >= 70 ? 'Yellow Review' : 'Red Pass'

  // Needle color matches the zone
  const needleColor = color

  return (
    <div className="m5-gauge">
      <div className="m5-gauge-arc">
        <svg className="m5-gauge-svg" viewBox="0 0 220 126" aria-hidden="true">
          <path
            className="m5-gauge-track"
            d="M 20 110 A 90 90 0 0 1 200 110"
            pathLength="100"
          />
          <path
            className="m5-gauge-progress"
            d="M 20 110 A 90 90 0 0 1 200 110"
            pathLength="100"
            style={{ '--arc-color': color, strokeDasharray: `${boundedScore} 100` }}
          />
        </svg>
        <div className="m5-gauge-needle" style={{ transform: `rotate(${rotation}deg)`, background: needleColor }} />
        <div className="m5-gauge-center">
          <strong style={{ color }}>{score}</strong>
          <span>/100</span>
        </div>
      </div>
      <div className="m5-gauge-labels">
        <span style={{ color: '#dc2626' }}>Pass</span>
        <span style={{ color: '#d97706' }}>Review</span>
        <span style={{ color: '#16a34a' }}>Buy</span>
      </div>
      {/* Score badge */}
      <div className="m5-score-badge" style={{ background: bg, color, borderColor: color }}>
        {scoreLabel}
      </div>
    </div>
  )
}

// ── Score formula definitions ────────────────────────────────────────────────
const SCORE_FORMULAS = {
  'Financial Strength': {
    weight: '34%',
    range: '45–100',
    formula: '(Cap Rate ÷ 8%) × 35  +  (DSCR ÷ 1.6) × 35  +  (Cash-on-Cash ÷ 12%) × 30',
    inputs: [
      { name: 'Cap Rate', ref: '8% benchmark', note: 'Entry cap rate from underwriting' },
      { name: 'DSCR', ref: '1.6× max', note: 'Debt Service Coverage Ratio from Module 4' },
      { name: 'Cash-on-Cash', ref: '12% target', note: 'Year 1 cash-on-cash return' },
    ],
    note: 'Measures income-producing power and lender coverage relative to target thresholds.',
  },
  'Market Position': {
    weight: '24%',
    range: '45–100',
    formula: 'AI Market Score × 70%  +  (Occupancy Rate × 100) × 30%',
    inputs: [
      { name: 'AI Market Score', ref: '0–100', note: 'From initial AI deal scoring in Module 1' },
      { name: 'Occupancy Rate', ref: '100% = full', note: 'Current occupancy from rent roll or OM' },
    ],
    note: 'Reflects market confidence and current lease-up performance.',
  },
  'Risk Control': {
    weight: '20%',
    range: '35–100',
    formula: '100  −  DSCR gap × 45  −  Cap Rate gap × 500  −  Occupancy gap × 2',
    inputs: [
      { name: 'DSCR gap', ref: 'vs 1.25×', note: 'Penalty if DSCR < 1.25 (capped at 0)' },
      { name: 'Cap Rate gap', ref: 'vs 5%', note: 'Penalty if cap rate < 5% (capped at 0)' },
      { name: 'Occupancy gap', ref: 'vs 88%', note: 'Penalty for occupancy below 88%' },
    ],
    note: 'Starts at 100 and deducts for each risk factor that falls below its threshold.',
  },
  'Value-Add Potential': {
    weight: '22%',
    range: '40–100',
    formula: '(IRR ÷ 16%) × 55  +  (Cash-on-Cash ÷ 10%) × 30  +  Vacancy Bonus (4–12)',
    inputs: [
      { name: 'Levered IRR', ref: '16% target', note: 'Total return over hold period' },
      { name: 'Cash-on-Cash', ref: '10% target', note: 'Year 1 cash yield on equity' },
      { name: 'Vacancy Bonus', ref: '+12 if occ < 94%', note: 'Upside credit when occupancy is below stabilized level' },
    ],
    note: 'Rewards deals with high return potential and room to grow income through operations.',
  },
}

function BreakdownBar({ label, value }) {
  const [showFormula, setShowFormula] = useState(false)
  const formula = SCORE_FORMULAS[label]
  const color = '#1d4ed8'
  const bg = '#EFF6FF'

  return (
    <div className="m5-breakdown-row">
      <div className="m5-breakdown-top">
        <span style={{ color }}>{label}</span>
        <div className="m5-breakdown-top-right">
          <strong style={{ color }}>{value}/100</strong>
          {formula && (
            <button
              className="m5-formula-btn"
              onClick={() => setShowFormula(v => !v)}
              title="How is this calculated?"
            >
              {showFormula ? '▲' : 'ƒ'}
            </button>
          )}
        </div>
      </div>
      <div className="m5-bar-track">
        <div className="m5-bar-fill" style={{ width: `${value}%`, background: 'linear-gradient(90deg, #1d4ed8, #60a5fa)' }} />
      </div>
      {/* Score tier label */}
      <div className="m5-bar-tier" style={{ color, background: bg }}>
        {value >= 75 ? 'Strong' : value >= 55 ? 'Moderate' : 'Weak'} · {formula?.weight || ''} of overall score
      </div>
      {/* Expandable formula card */}
      {showFormula && formula && (
        <div className="m5-formula-card">
          <div className="m5-formula-header">
            <span>📐 How is {label} calculated?</span>
            <em>Weight: {formula.weight} · Range: {formula.range}</em>
          </div>
          <div className="m5-formula-eq">{formula.formula}</div>
          <div className="m5-formula-inputs">
            {formula.inputs.map(inp => (
              <div key={inp.name} className="m5-formula-input-row">
                <strong>{inp.name}</strong>
                <span className="m5-formula-ref">{inp.ref}</span>
                <em>{inp.note}</em>
              </div>
            ))}
          </div>
          <p className="m5-formula-note">{formula.note}</p>
        </div>
      )}
    </div>
  )
}

// ── Feature 2: Score sensitivity — how each driver affects overall score ────
function calcSensitivity(breakdown) {
  const weights = { financialStrength: 0.34, marketPosition: 0.24, riskLevel: 0.20, valueAdd: 0.22 }
  const DELTA = 10
  return Object.entries(weights).map(([key, w]) => {
    const up   = clamp(Math.round(breakdown[key] + DELTA), 0, 100) * w
    const base = breakdown[key] * w
    const down = clamp(Math.round(breakdown[key] - DELTA), 0, 100) * w
    return {
      key,
      label: { financialStrength: 'Financial Strength', marketPosition: 'Market Position', riskLevel: 'Risk Control', valueAdd: 'Value-Add Potential' }[key],
      weight: Math.round(w * 100),
      current: breakdown[key],
      scoreImpactUp:   +up   - +base,
      scoreImpactDown: +down - +base,
    }
  })
}

function downloadInvestmentMemo(dealState, baselineReview, orgProfile) {
  if (!baselineReview) return
  const dealName = dealState.dealName || 'Active Deal'
  const org = orgProfile || {}
  const brandColor = org.brandColor || '#1B3A6B'
  const companyName = org.companyName || 'AcquireOS AI'
  const senderName = org.senderName || ''
  const website = org.website || ''
  const phone = org.phone || ''
  const contactEmail = org.contactEmail || ''
  const address = org.officeAddress || ''

  // Build investmentMemo from available data when not provided by backend
  const memo = baselineReview.investmentMemo || {}
  const title = memo.title || `Investment Baseline Memo — ${dealName}`

  // Build sections from memo if available, otherwise construct from baseline data
  let memoSections = memo.sections || []
  if (!memoSections.length) {
    const b = baselineReview.baseline || {}
    const fmt = v => v != null ? String(v) : 'N/A'
    const builtSections = []
    builtSections.push({
      title: 'Baseline Financial Summary',
      body: 'Key underwriting metrics computed from available deal data.',
      bullets: [
        `Year 1 NOI: ${b.noiYear1 != null ? '$' + Number(b.noiYear1).toLocaleString() : 'N/A'}`,
        `Entry Cap Rate: ${b.entryCapRate != null ? (Number(b.entryCapRate) * 100).toFixed(2) + '%' : 'N/A'}`,
        `Minimum DSCR: ${b.minDscr != null ? fmt(b.minDscr) + 'x' : 'N/A'}`,
        `Levered IRR: ${b.leveredIrr != null ? (Number(b.leveredIrr) * 100).toFixed(1) + '%' : 'N/A'}`,
      ].filter(Boolean),
    })
    if ((baselineReview.discrepancies || []).length) {
      builtSections.push({
        title: 'Discrepancies',
        body: 'Gaps identified between sponsor claims and system baseline.',
        bullets: baselineReview.discrepancies,
      })
    }
    if ((baselineReview.requiredActions || []).length) {
      builtSections.push({
        title: 'Required Actions',
        body: 'Items that must be resolved before final recommendation.',
        bullets: baselineReview.requiredActions,
      })
    }
    if ((baselineReview.risks || []).length) {
      builtSections.push({
        title: 'Risks',
        body: '',
        bullets: baselineReview.risks,
      })
    }
    if ((baselineReview.criticalQuestions || []).length) {
      builtSections.push({
        title: 'Critical Questions',
        body: '',
        bullets: baselineReview.criticalQuestions,
      })
    }
    memoSections = builtSections
  }

  const rows = (baselineReview.marketingVsReality || []).map(row => `
    <tr>
      <td>${escapeHtml(row.metric)}</td>
      <td>${escapeHtml(row.sponsorClaim)}</td>
      <td>${escapeHtml(row.systemBaseline)}</td>
      <td>${escapeHtml(row.note)}</td>
    </tr>
  `).join('')
  const sections = memoSections.map(section => `
    <h2>${escapeHtml(section.title)}</h2>
    ${section.body ? `<p>${escapeHtml(section.body)}</p>` : ''}
    ${(section.bullets || []).length ? `<ul>${section.bullets.map(item => `<li>${escapeHtml(item)}</li>`).join('')}</ul>` : ''}
  `).join('')
  const html = `
    <html>
      <head>
        <meta charset="utf-8" />
        <style>
          body { font-family: Arial, sans-serif; color: #1f2933; line-height: 1.45; margin: 0; padding: 0; }
          .doc-header { background: ${brandColor}; color: #fff; padding: 22px 28px; }
          .doc-header h1 { margin: 0 0 4px; font-size: 22px; }
          .doc-header p { margin: 0; font-size: 12px; opacity: 0.85; }
          .doc-body { padding: 28px; }
          h2 { font-size: 16px; margin-top: 22px; border-bottom: 2px solid ${brandColor}; padding-bottom: 5px; color: ${brandColor}; }
          p { font-size: 12px; }
          table { width: 100%; border-collapse: collapse; margin-top: 12px; font-size: 11px; }
          th, td { border: 1px solid #d9e2df; padding: 7px; vertical-align: top; }
          th { background: #edf7f4; text-align: left; }
          li { margin-bottom: 5px; font-size: 12px; }
          .doc-footer { border-top: 2px solid ${brandColor}; margin-top: 32px; padding-top: 14px; font-size: 11px; color: #6b7280; display: flex; gap: 16px; flex-wrap: wrap; }
          .doc-footer strong { color: ${brandColor}; }
        </style>
      </head>
      <body>
        <div class="doc-header">
          <h1>${escapeHtml(companyName)}</h1>
          <p>Investment Baseline Memo${website ? ' · ' + escapeHtml(website) : ''}</p>
        </div>
        <div class="doc-body">
          <h1 style="color:${brandColor};font-size:20px;margin-top:0">${escapeHtml(title)}</h1>
          <p><strong>Deal:</strong> ${escapeHtml(dealName)}</p>
          <p><strong>Prepared by:</strong> ${escapeHtml(senderName || companyName)}</p>
          <p><strong>Date:</strong> ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          <p><strong>Recommendation:</strong> ${escapeHtml(baselineReview.verdictCode || '')} — ${escapeHtml(baselineReview.verdict || '')}</p>
          <h2>Marketing vs Reality</h2>
          <table>
            <thead>
              <tr>
                <th>Metric</th>
                <th>Sponsor Claim</th>
                <th>System Baseline</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
          ${sections}
          <div class="doc-footer">
            ${senderName ? `<span><strong>${escapeHtml(senderName)}</strong></span>` : ''}
            <span>${escapeHtml(companyName)}</span>
            ${phone ? `<span>${escapeHtml(phone)}</span>` : ''}
            ${contactEmail ? `<span>${escapeHtml(contactEmail)}</span>` : ''}
            ${address ? `<span>${escapeHtml(address)}</span>` : ''}
          </div>
        </div>
      </body>
    </html>
  `
  const blob = new Blob([html], { type: 'application/msword;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  const safeName = dealName.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '') || 'deal'
  link.href = url
  link.download = `${safeName}-Investment-Baseline-Memo.doc`
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

function Module5Page() {
  const { dealState, updateDealState, updateAvailableModules, forceNavigateToModule } = useDealStore()
  const { profile: orgProfile } = useOrganizationStore()
  const analysis = calculateScore(dealState)
  // Task 5: gate on admin-configurable minimum score
  const effectiveMinScore = Math.max(DEAL_SCORE_MINIMUM, MIN_LENDER_MATCH_SCORE)
  const canAdvanceToLenders = analysis.score >= effectiveMinScore
  const isExceptionalDeal = analysis.score >= DEAL_SCORE_MAXIMUM
  const price = Number(dealState.rawDealData?.price) || 0
  const noi = maxZero(dealState.underwritingMetrics?.noi || dealState.extractedFinancials?.noi)
  const parsedDocuments = dealState.financialDocuments?.parsedDocuments || {}
  const sourceText = (fieldKey) => getSourceSummary(parsedDocuments, fieldKey)
  const discount = price ? (1 - analysis.recommended / price) * 100 : 0
  const offerLogic = analysis.pricingLogic
  const offerSpreadLabel = `${(offerLogic.floorPct * 100).toFixed(0)}% floor / ${(offerLogic.ceilingPct * 100).toFixed(0)}% ceiling`

  // ── Renegotiation System ──────────────────────────────────────────────────────
  const savedScore = dealState.scoreOverrides || {}
  const [showRenegotiate, setShowRenegotiate]     = useState(false)
  const [overrideAck, setOverrideAck]             = useState(savedScore.overrideAck ?? false)
  const [overrideConfirmed, setOverrideConfirmed] = useState(savedScore.overrideConfirmed ?? false)

  useEffect(() => {
    updateDealState({ scoreOverrides: { overrideAck, overrideConfirmed } })
  }, [overrideAck, overrideConfirmed]) // eslint-disable-line
  const renegoEnabled = !canAdvanceToLenders   // only interactive when score fails
  // Score >= 75 → can advance. Score < 75 → must renegotiate or override.
  const canAdvanceNow = analysis.score >= 75 || overrideConfirmed
  const needsRenegotiation = analysis.score < 75 && !overrideConfirmed

  // Binary-search the break-even price (memoized — only recomputes when price/noi change)
  const targetPrice = useMemo(() => {
    if (!price || !noi) return null
    let lo = price * 0.50, hi = price
    for (let i = 0; i < 30; i++) {
      const mid = (lo + hi) / 2
      const s = calculateScore({ ...dealState, rawDealData: { ...dealState.rawDealData, price: mid } })
      if (s.score >= effectiveMinScore) lo = mid; else hi = mid
    }
    const found = Math.round((lo + hi) / 2 / 1000) * 1000
    return found < price * 0.99 ? found : null
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [price, noi, effectiveMinScore])

  // Renegotiation form — pre-fill with target price when available
  const [renego, setRenego] = useState({
    price:       targetPrice || price || '',
    concessions: 0,
    reason:      '',
  })

  // Live-recalculate score whenever renego.price changes
  const renegoDealState = useMemo(() => ({
    ...dealState,
    rawDealData: { ...dealState.rawDealData, price: Number(renego.price) || price },
  }), [dealState, renego.price, price])

  const renegoAnalysis   = useMemo(() => calculateScore(renegoDealState), [renegoDealState])
  const renegoPassesGate = renegoAnalysis.score >= effectiveMinScore

  // Live cap rate at renegotiated price (for the metrics tiles)
  const renegoCapRate = useMemo(() => {
    const p = Number(renego.price) || price
    return p > 0 ? noi / p : 0
  }, [renego.price, noi, price])

  // Calculation log drawer
  const [logOpen, setLogOpen] = useState(false)

  // Fix 9: user-editable offer override
  const [offerOverride, setOfferOverride] = useState(null)
  const displayOffer = offerOverride ?? analysis.recommended
  const [baselineReview, setBaselineReview] = useState(dealState.scoreAndOffer?.investmentBaselineReview || null)
  const [, setBaselineLoading] = useState(false)

  // Feature 2
  const sensitivity = calcSensitivity(analysis.breakdown)

  useEffect(() => {
    let cancelled = false
    setBaselineLoading(true)
    analyzeInvestmentBaseline(dealState)
      .then((result) => {
        if (!cancelled) setBaselineReview(result)
      })
      .finally(() => {
        if (!cancelled) setBaselineLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [dealState.dealId, dealState.underwritingMetrics?.noi])

  // Shared save + advance logic
  const saveAndAdvance = (useRenegoPrice = false, isOverride = false) => {
    const activeAnalysis = useRenegoPrice ? renegoAnalysis : analysis

    // If renegotiated, update rawDealData price so downstream modules use it
    if (useRenegoPrice && renego.price && Number(renego.price) !== price) {
      updateDealState({
        rawDealData: { ...dealState.rawDealData, price: Number(renego.price) },
        renegotiation: {
          renegotiatedPrice:  Number(renego.price),
          sellerConcessions:  Number(renego.concessions) || 0,
          renegotiationReason: renego.reason,
          originalPrice:       price,
          priceReduction:      price - Number(renego.price),
          renegotiatedAt:      new Date().toISOString(),
        },
      })
    }

    updateDealState({
      scoreAndOffer: {
        numericalScore: activeAnalysis.score,
        verdict:        activeAnalysis.verdictCode,
        scoreBreakdown: activeAnalysis.breakdown,
        strengths:      activeAnalysis.strengths,
        risks:          activeAnalysis.risks,
        isOverride,
        overrideReason: isOverride ? renego.reason : null,
        renegotiated:   useRenegoPrice,
        offerPrice: {
          recommended: activeAnalysis.recommended,
          minimum:     activeAnalysis.minimum,
          maximum:     activeAnalysis.maximum,
          confidence:  activeAnalysis.confidence,
        },
        dealScoreVerdictDetails: {
          scoringModel:       'module-5-ai-score-v1',
          keyDrivers:         ['Underwriting metrics', 'Market intelligence', 'Property cash flow', 'Offer pricing rationale'],
          redFlags:           activeAnalysis.risks,
          greenFlags:         activeAnalysis.strengths,
          pricingFactors:     activeAnalysis.pricingFactors,
          plainEnglishVerdict:activeAnalysis.verdict,
          explanation:        activeAnalysis.explanation,
        },
        investmentBaselineReview: baselineReview,
      },
    })

    updateAvailableModules()
    setTimeout(() => forceNavigateToModule(6), 500)
  }

  // Called from the calculator: save the new price and send user back to
  // Underwriting (Module 4) so metrics get recalculated at the negotiated price.
  const handleProceedToReunderwrite = () => {
    if (!renego.price) return
    updateDealState({
      rawDealData: { ...dealState.rawDealData, price: Number(renego.price) },
      renegotiation: {
        renegotiatedPrice:   Number(renego.price),
        sellerConcessions:   Number(renego.concessions) || 0,
        renegotiationReason: renego.reason,
        originalPrice:       price,
        priceReduction:      price - Number(renego.price),
        renegotiatedAt:      new Date().toISOString(),
      },
    })
    setShowRenegotiate(false)
    setTimeout(() => forceNavigateToModule(4), 300)
  }

  const handleComplete = () => {
    if (!canAdvanceNow) return
    saveAndAdvance(false, overrideConfirmed)
  }

  return (
    <div className="m5-page">
      <div className="m5-header">
        <div className="m5-badge">Module 4</div>
        <div>
          <h1>Deal Score &amp; Verdict</h1>
          <p>AI-powered go/no-go decision support for the active investment deal.</p>
        </div>
        <div className="m5-deal-chip">
          <span>Active Deal</span>
          <strong>{dealState.dealName || 'Selected Deal'}</strong>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          RENEGOTIATION CALCULATOR — opens as a modal when triggered
      ════════════════════════════════════════════════════════════════════════ */}
      {showRenegotiate && (
        <div
          onClick={e => e.target === e.currentTarget && setShowRenegotiate(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
            padding: '40px 20px',
            overflowY: 'auto',
          }}
        >
        <div style={{
          width: '100%',
          maxWidth: 920,
          borderRadius: 16,
          overflow: 'hidden',
          border: `2px solid ${renegoEnabled ? '#f59e0b' : '#e2e8f0'}`,
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}>
          {/* ── Modal close row ── */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '10px 16px', background: '#f8fafc',
            borderBottom: '1px solid #e2e8f0',
          }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: '#475569' }}>
              Renegotiation Calculator
            </span>
            <button
              onClick={() => setShowRenegotiate(false)}
              style={{
                background: '#fff', border: '1px solid #e2e8f0',
                borderRadius: 8, padding: '5px 14px',
                cursor: 'pointer', fontWeight: 700, color: '#64748b', fontSize: 13,
              }}
            >
              ✕ Close
            </button>
          </div>

          {/* ── Header bar ── */}
          <div style={{
            background: renegoEnabled
              ? 'linear-gradient(135deg, #92400e 0%, #b45309 100%)'
              : 'linear-gradient(135deg, #475569 0%, #64748b 100%)',
            padding: '16px 24px',
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <span style={{ fontSize: 26 }}>{renegoEnabled ? '🔄' : '🔒'}</span>
            <div style={{ flex: 1 }}>
              <div style={{ color: '#fff', fontWeight: 800, fontSize: 16, marginBottom: 2 }}>
                Renegotiation Calculator
              </div>
              <div style={{ color: renegoEnabled ? '#fde68a' : '#cbd5e1', fontSize: 13 }}>
                {renegoEnabled
                  ? `Score ${analysis.score}/100 is below ${effectiveMinScore} — lower the purchase price to qualify, or override with client approval.`
                  : `Score ${analysis.score}/100 already meets the ${effectiveMinScore} threshold. Renegotiation is not needed.`}
              </div>
            </div>
            {!renegoEnabled && (
              <div style={{
                background: '#dcfce7', color: '#16a34a', fontWeight: 700,
                fontSize: 12, padding: '4px 12px', borderRadius: 20, whiteSpace: 'nowrap',
              }}>
                ✓ Score Qualifies
              </div>
            )}
          </div>

          {/* ── Two-column body ── */}
          <div style={{
            background: renegoEnabled
              ? 'linear-gradient(135deg, #fefce8 0%, #fff7ed 100%)'
              : '#f8fafc',
            padding: '24px',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 24,
            pointerEvents: renegoEnabled ? 'auto' : 'none',
          }}>

            {/* ─── LEFT: Input fields ─────────────────────────────────────── */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ fontWeight: 800, color: '#92400e', fontSize: 14, letterSpacing: 0.3 }}>
                STEP 1 — Adjust Purchase Price
              </div>

              {/* Hint: target price from binary search */}
              {targetPrice ? (
                <div style={{
                  background: '#dcfce7', border: '1px solid #86efac',
                  borderRadius: 8, padding: '10px 14px', fontSize: 13,
                }}>
                  <span style={{ fontWeight: 700, color: '#15803d' }}>💡 Suggested Target: </span>
                  <span style={{ color: '#166534' }}>
                    Offer <strong>${targetPrice.toLocaleString()}</strong> or lower to reach score {effectiveMinScore}
                    &nbsp;(saves <strong>${(price - targetPrice).toLocaleString()}</strong> off asking)
                  </span>
                </div>
              ) : price > 0 ? (
                <div style={{
                  background: '#fef2f2', border: '1px solid #fecaca',
                  borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#991b1b',
                }}>
                  ⚠️ Price alone may not get the score to {effectiveMinScore}. Use the Override option below.
                </div>
              ) : null}

              {/* Counter Offer Price */}
              <div>
                <label style={{
                  fontSize: 12, fontWeight: 700, color: '#78350f',
                  display: 'flex', justifyContent: 'space-between', marginBottom: 6,
                }}>
                  <span>Counter Offer Price ($) <span style={{ color: '#dc2626' }}>*</span></span>
                  {price > 0 && (
                    <span style={{ color: '#d97706', fontWeight: 400 }}>
                      Asking: ${price.toLocaleString()}
                    </span>
                  )}
                </label>
                <input
                  type="number"
                  value={renego.price}
                  onChange={e => setRenego(r => ({ ...r, price: e.target.value }))}
                  placeholder={targetPrice ? String(targetPrice) : 'Enter your counter offer'}
                  style={{
                    width: '100%', padding: '11px 14px', boxSizing: 'border-box',
                    border: `2px solid ${renegoPassesGate ? '#22c55e' : '#f59e0b'}`,
                    borderRadius: 8, fontSize: 15, outline: 'none',
                    background: renegoPassesGate ? '#f0fdf4' : '#fffbeb',
                    fontWeight: 600, color: '#0f172a',
                    transition: 'border-color 0.2s, background 0.2s',
                  }}
                />
                {price > 0 && Number(renego.price) > 0 && (
                  <div style={{ fontSize: 11, color: '#d97706', marginTop: 4 }}>
                    {Number(renego.price) < price
                      ? `▼ ${((1 - Number(renego.price)/price)*100).toFixed(1)}% below asking price`
                      : Number(renego.price) === price
                        ? '= Same as asking price'
                        : `▲ ${((Number(renego.price)/price - 1)*100).toFixed(1)}% above asking price`}
                  </div>
                )}
              </div>

              {/* Seller Concessions */}
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#78350f', display: 'block', marginBottom: 6 }}>
                  Seller Concessions / Credits ($) <span style={{ fontWeight: 400, color: '#92400e' }}>(optional)</span>
                </label>
                <input
                  type="number"
                  value={renego.concessions}
                  onChange={e => setRenego(r => ({ ...r, concessions: e.target.value }))}
                  placeholder="e.g. 50,000"
                  style={{
                    width: '100%', padding: '11px 14px', boxSizing: 'border-box',
                    border: '2px solid #f59e0b', borderRadius: 8, fontSize: 14,
                    outline: 'none', background: '#fffbeb',
                  }}
                />
              </div>

              {/* Reason */}
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#78350f', display: 'block', marginBottom: 6 }}>
                  Renegotiation Reason / Notes
                </label>
                <textarea
                  rows={3}
                  value={renego.reason}
                  onChange={e => setRenego(r => ({ ...r, reason: e.target.value }))}
                  placeholder="e.g. Deferred maintenance, roof replacement needed, vacancy higher than stated..."
                  style={{
                    width: '100%', padding: '10px 14px', boxSizing: 'border-box',
                    border: '2px solid #f59e0b', borderRadius: 8, fontSize: 13,
                    outline: 'none', background: '#fffbeb', fontFamily: 'inherit',
                    resize: 'vertical',
                  }}
                />
              </div>
            </div>

            {/* ─── RIGHT: Live recalculated score ─────────────────────────── */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ fontWeight: 800, color: '#92400e', fontSize: 14, letterSpacing: 0.3 }}>
                STEP 2 — Recalculated Score (Live)
              </div>

              {/* Score comparison cards */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={{
                  background: '#fff', border: '2px solid #fecaca', borderRadius: 12,
                  padding: '16px', textAlign: 'center',
                }}>
                  <div style={{ fontSize: 11, color: '#ef4444', fontWeight: 700, marginBottom: 6, letterSpacing: 0.5 }}>
                    ORIGINAL
                  </div>
                  <div style={{ fontSize: 36, fontWeight: 900, color: '#dc2626', lineHeight: 1 }}>
                    {analysis.score}
                  </div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>/ 100</div>
                  <div style={{ fontSize: 11, marginTop: 6, fontWeight: 600, color: canAdvanceToLenders ? '#16a34a' : '#ef4444' }}>
                    {canAdvanceToLenders ? '✓ Meets threshold' : 'Below threshold'}
                  </div>
                </div>

                <div style={{
                  background: '#fff',
                  border: `2px solid ${renegoPassesGate ? '#22c55e' : '#fde68a'}`,
                  borderRadius: 12, padding: '16px', textAlign: 'center',
                  transition: 'border-color 0.3s',
                }}>
                  <div style={{
                    fontSize: 11, fontWeight: 700, marginBottom: 6, letterSpacing: 0.5,
                    color: renegoPassesGate ? '#16a34a' : '#d97706',
                  }}>
                    RENEGOTIATED
                  </div>
                  <div style={{
                    fontSize: 36, fontWeight: 900, lineHeight: 1,
                    color: renegoPassesGate ? '#16a34a' : '#d97706',
                    transition: 'color 0.3s',
                  }}>
                    {renegoAnalysis.score}
                  </div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>/ 100</div>
                  <div style={{
                    fontSize: 11, marginTop: 6, fontWeight: 600,
                    color: renegoPassesGate ? '#16a34a' : '#d97706',
                  }}>
                    {renegoPassesGate ? `✓ Passes threshold!` : `Needs ${effectiveMinScore - renegoAnalysis.score} more pts`}
                  </div>
                </div>
              </div>

              {/* Live metric tiles */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {[
                  {
                    label: 'Cap Rate',
                    value: `${(renegoCapRate * 100).toFixed(2)}%`,
                    good: renegoCapRate >= 0.05,
                    note: renegoCapRate >= 0.05 ? '✓ ≥ 5%' : `✗ < 5%`,
                  },
                  {
                    label: 'Price Reduction',
                    value: price > 0 && Number(renego.price) > 0
                      ? `-$${Math.max(0, price - Number(renego.price)).toLocaleString()}`
                      : '—',
                    good: Number(renego.price) > 0 && Number(renego.price) < price,
                    note: Number(renego.price) < price ? '✓ Below asking' : '—',
                  },
                  {
                    label: 'Score Change',
                    value: `${renegoAnalysis.score - analysis.score >= 0 ? '+' : ''}${renegoAnalysis.score - analysis.score} pts`,
                    good: renegoAnalysis.score > analysis.score,
                    note: renegoAnalysis.score > analysis.score ? '✓ Improving' : 'No change',
                  },
                  {
                    label: 'vs Threshold',
                    value: `${renegoAnalysis.score} / ${effectiveMinScore}`,
                    good: renegoPassesGate,
                    note: renegoPassesGate ? '✓ Qualifies' : '✗ Still below',
                  },
                ].map(m => (
                  <div key={m.label} style={{
                    background: m.good ? '#f0fdf4' : '#fef9f0',
                    border: `1px solid ${m.good ? '#bbf7d0' : '#fde68a'}`,
                    borderRadius: 8, padding: '10px 12px',
                    transition: 'background 0.2s, border-color 0.2s',
                  }}>
                    <div style={{ fontSize: 10, color: '#64748b', marginBottom: 2, fontWeight: 600 }}>{m.label}</div>
                    <div style={{
                      fontSize: 15, fontWeight: 800,
                      color: m.good ? '#15803d' : '#d97706',
                    }}>{m.value}</div>
                    <div style={{ fontSize: 10, color: m.good ? '#16a34a' : '#d97706', marginTop: 2 }}>{m.note}</div>
                  </div>
                ))}
              </div>

              {/* ── Proceed to re-underwrite at renegotiated price ── */}
              {(() => {
                const btnActive = !!renego.price && needsRenegotiation
                return (
                  <button
                    disabled={!btnActive}
                    onClick={handleProceedToReunderwrite}
                    style={{
                      width: '100%', padding: '13px 20px', borderRadius: 10, border: 'none',
                      background: btnActive
                        ? 'linear-gradient(135deg, #1d4ed8, #1e40af)'
                        : '#e2e8f0',
                      color: btnActive ? '#fff' : '#94a3b8',
                      fontWeight: 800, fontSize: 15,
                      cursor: btnActive ? 'pointer' : 'not-allowed',
                      boxShadow: btnActive ? '0 4px 14px rgba(29,78,216,0.35)' : 'none',
                      transition: 'all 0.2s',
                    }}
                  >
                    {!needsRenegotiation
                      ? '✓ Score ≥ 75 — No renegotiation needed'
                      : renego.price
                        ? `🔁 Save Price $${Number(renego.price).toLocaleString()} & Re-run Underwriting`
                        : 'Enter a counter offer price above'}
                  </button>
                )
              })()}

              {/* ── STEP 3: Client Override ── */}
              <div style={{
                background: '#fff', border: '1px solid #fecaca',
                borderRadius: 10, padding: '16px',
              }}>
                <div style={{
                  fontWeight: 800, color: '#dc2626', fontSize: 13,
                  marginBottom: 8, letterSpacing: 0.3,
                }}>
                  STEP 3 — CLIENT OVERRIDE (Proceed Anyway)
                </div>
                <p style={{ margin: '0 0 12px', fontSize: 12, color: '#7f1d1d', lineHeight: 1.6 }}>
                  If your client wants to proceed despite a score below {effectiveMinScore},
                  check the box to acknowledge the risk. This is logged on the deal record.
                </p>
                <label style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  cursor: 'pointer', fontSize: 13, color: '#991b1b',
                }}>
                  <input
                    type="checkbox"
                    checked={overrideAck}
                    onChange={e => setOverrideAck(e.target.checked)}
                    style={{ marginTop: 2, width: 16, height: 16, cursor: 'pointer', flexShrink: 0 }}
                  />
                  <span>
                    I acknowledge this deal scores <strong>{analysis.score}/100</strong> (below {effectiveMinScore}).
                    My client has reviewed all risks and instructed us to proceed as-is.
                  </span>
                </label>
                {overrideAck && (
                  <button
                    onClick={() => {
                      setOverrideConfirmed(true)
                      setShowRenegotiate(false)
                      saveAndAdvance(false, true)
                    }}
                    style={{
                      marginTop: 12, width: '100%', padding: '11px',
                      borderRadius: 8, border: '2px solid #dc2626',
                      background: '#fff', color: '#dc2626',
                      fontWeight: 800, fontSize: 14, cursor: 'pointer',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#fef2f2' }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#fff' }}
                  >
                    ⚠️ Override — Proceed with Score {analysis.score}/100
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        </div>
      )}


      <div className="m5-content">
        <section className="m5-score-panel">
          <div className="m5-panel-title">
            <span>Overall Deal Score</span>
          </div>
          <ScoreGauge score={analysis.score} />
          <div className="m5-verdict-box">
            <span>Plain English Verdict</span>
            <p>{analysis.verdict}</p>
          </div>
          <p className="m5-ai-explanation">{analysis.explanation}</p>
        </section>

        <section className="m5-offer-panel">
          <div className="m5-panel-title">
            <span>Recommended Offer Range</span>
            <strong>{analysis.confidence} Clarity</strong>
          </div>
          <div className="m5-offer-amount">
            <EditableField
              value={displayOffer}
              type="number"
              onSave={v => setOfferOverride(v)}
              displayFormat={v => money(v)}
              style={{ fontSize: 'inherit', fontWeight: 'inherit' }}
              placeholder="Enter offer price"
            />
          </div>
          <div className="m5-offer-sub">
            {offerOverride
              ? `Adjusted from AI recommendation of ${money(analysis.recommended)}`
              : discount > 0 ? `${discount.toFixed(1)}% below asking price` : 'Aligned with current asking price'}
            <InfoHint label="Why did this offer percentage appear?" title="Why this percentage?">
              <p>
                This deal scored <strong>{analysis.score}/100 ({analysis.verdict})</strong>.
              </p>
              <p>
                <strong>{money(analysis.recommended)}</strong>
              </p>
              <p>
                Score ≥85 → 2% below asking. Score 72-84 → 6% below asking.
                Score 60-71 → 12% below asking. Score &lt;60 → 20% below asking.
              </p>
            </InfoHint>
          </div>
          <div className="m5-offer-range">
            <div>
              <span>
                Minimum
                <InfoHint label="How is the minimum offer calculated?" title="Minimum offer logic">
                  <p>
                    <strong>ALWAYS 6% BELOW THE RECOMMENDED OFFER.</strong> THIS IS THE LOWEST YOU CAN OFFER WITHOUT SIGNIFICANTLY RISKING THE DEAL.
                  </p>
                </InfoHint>
              </span>
              <strong><EditableField value={analysis.minimum} type="number" onSave={v => {}} displayFormat={v => money(v)} /></strong>
            </div>
            <div>
              <span>
                Maximum
                <InfoHint label="How is the maximum offer calculated?" title="Maximum offer logic">
                  <p>
                    <strong>ALWAYS 4% ABOVE THE RECOMMENDED OFFER.</strong> THE MOST YOU SHOULD PAY EVEN IN A COMPETITIVE BIDDING SITUATION.
                  </p>
                </InfoHint>
              </span>
              <strong><EditableField value={analysis.maximum} type="number" onSave={v => {}} displayFormat={v => money(v)} /></strong>
            </div>
          </div>
          <div className="m5-offer-logic-note">
            Offer spread: {offerSpreadLabel}. Recommendation changes by deal score and underwriting metrics.
          </div>

          {/* ── Mortgage Calculator ── */}
          <MortgageCalculator offerPrice={displayOffer} />
        </section>

        <section className="m5-price-logic-panel">
          <div className="m5-panel-title">
            <span>Offer Pricing Rationale</span>
            <strong>Price Cushion</strong>
          </div>
          {analysis.pricingFactors.map(factor => (
            <div className="m5-price-factor" key={factor.label}>
              <div>
                <span>{factor.label}</span>
                <em>{factor.note}</em>
              </div>
              <strong>{factor.value}/100</strong>
            </div>
          ))}
        </section>

        <section className="m5-breakdown-panel">
          <div className="m5-panel-title">
            <span>Combined Score Breakdown</span>
            <strong>AI Weighted</strong>
          </div>
          <BreakdownBar label="Financial Strength"   value={analysis.breakdown.financialStrength} />
          <BreakdownBar label="Market Position"      value={analysis.breakdown.marketPosition} />
          <BreakdownBar label="Risk Control"         value={analysis.breakdown.riskLevel} />
          <BreakdownBar label="Value-Add Potential"  value={analysis.breakdown.valueAdd} />

          {/* ── Overall Score Formula ── */}
          <div className="m5-overall-formula">
            <div className="m5-formula-header">
              <span>🧮 Overall Score Formula</span>
              <em>How {analysis.score}/100 is computed</em>
            </div>
            <div className="m5-overall-formula-eq">
              <div className="m5-ofr-row" style={{ '--col': scoreColor(analysis.breakdown.financialStrength) }}>
                <span>Financial Strength</span>
                <strong>{analysis.breakdown.financialStrength}</strong>
                <em>× 34%</em>
                <span className="m5-ofr-contrib">= {(analysis.breakdown.financialStrength * 0.34).toFixed(1)} pts</span>
              </div>
              <div className="m5-ofr-row" style={{ '--col': scoreColor(analysis.breakdown.marketPosition) }}>
                <span>Market Position</span>
                <strong>{analysis.breakdown.marketPosition}</strong>
                <em>× 24%</em>
                <span className="m5-ofr-contrib">= {(analysis.breakdown.marketPosition * 0.24).toFixed(1)} pts</span>
              </div>
              <div className="m5-ofr-row" style={{ '--col': scoreColor(analysis.breakdown.valueAdd) }}>
                <span>Value-Add Potential</span>
                <strong>{analysis.breakdown.valueAdd}</strong>
                <em>× 22%</em>
                <span className="m5-ofr-contrib">= {(analysis.breakdown.valueAdd * 0.22).toFixed(1)} pts</span>
              </div>
              <div className="m5-ofr-row" style={{ '--col': scoreColor(analysis.breakdown.riskLevel) }}>
                <span>Risk Control</span>
                <strong>{analysis.breakdown.riskLevel}</strong>
                <em>× 20%</em>
                <span className="m5-ofr-contrib">= {(analysis.breakdown.riskLevel * 0.20).toFixed(1)} pts</span>
              </div>
              <div className={`m5-ofr-total ${
                analysis.score > 75 ? 'score-buy' : analysis.score >= 70 ? 'score-review' : 'score-pass'
              }`}>
                <span>Overall Score</span>
                <strong>{analysis.score} / 100</strong>
              </div>
            </div>
            <p className="m5-formula-note">
              Scores are clamped to their valid range before weighting. The final score is rounded to the nearest whole number and capped between 1–100.
            </p>
          </div>
        </section>

        <section className="m5-list-panel m5-strengths">
          <div className="m5-panel-title">
            <span>Key Strengths</span>
            <strong>Green Checks</strong>
          </div>
          {analysis.strengths.map((item) => (
            <div className="m5-list-item" key={item}>
              <span className="m5-check">OK</span>
              <p>{item}</p>
            </div>
          ))}
        </section>

        <section className="m5-list-panel m5-risks">
          <div className="m5-panel-title">
            <span>Deal Risk Flags</span>
            <strong>Review Items</strong>
          </div>
          {analysis.risks.map((item) => (
            <div className="m5-list-item" key={item}>
              <span className="m5-warning">!</span>
              <p>{item}</p>
            </div>
          ))}
        </section>

        <section className="m5-baseline-panel">
          <div className="m5-panel-title">
            <span>Investment Baseline Review</span>
          </div>
          {baselineReview ? (() => {
            const hasBaselinePrice = Number(baselineReview.baseline?.purchasePrice || dealState.rawDealData?.price || 0) > 0
            return (
            <>
              <p className="m5-ai-explanation">{baselineReview.verdict}</p>
              <div className="m5-baseline-grid">
                <BaselineMetric
                  label="Baseline NOI"
                  value={metricDisplay(baselineReview.baseline?.noiYear1, money)}
                  title="Baseline NOI"
                >
                  <p>Net operating income from the stored underwriting or extracted financials. This is the main income baseline used for scoring.</p>
                  <p className="m5-source-line">Source: {sourceText('noi')}</p>
                </BaselineMetric>
                <BaselineMetric
                  label="Entry Cap"
                  value={metricDisplay(baselineReview.baseline?.entryCapRate, percent)}
                  title="Entry cap rate"
                >
                  <p>NOI divided by asking price. If it shows TBD, the deal does not have a usable asking price yet.</p>
                  <p className="m5-source-line">Source: NOI from {sourceText('noi')}; asking price from {sourceText('askingPrice')}.</p>
                </BaselineMetric>
                <BaselineMetric
                  label="Min DSCR"
                  value={hasBaselinePrice ? metricDisplay(baselineReview.baseline?.minDscr, value => `${numDisplay(value)}x`) : 'TBD'}
                  title="Minimum DSCR"
                >
                  <p>Debt service coverage ratio: NOI divided by annual debt service. TBD means price or loan assumptions are missing, so debt coverage would be misleading.</p>
                  <p className="m5-source-line">TBD here means the app needs asking price plus loan terms before calculating DSCR.</p>
                </BaselineMetric>
                <BaselineMetric
                  label="Levered IRR"
                  value={hasBaselinePrice ? metricDisplay(baselineReview.baseline?.leveredIrr, percent) : 'TBD'}
                  title="Levered IRR"
                >
                  <p>Projected internal rate of return after debt. TBD means the app needs a real purchase price and loan basis before this return is reliable.</p>
                  <p className="m5-source-line">Source: calculated from price, NOI, debt, and exit assumptions.</p>
                </BaselineMetric>
                {dealState.ownershipCosts?.annualInsurance && (
                  <BaselineMetric
                    label="Annual Insurance"
                    value={money(dealState.ownershipCosts.annualInsurance)}
                    title="Annual insurance"
                    estimated={dealState.ownershipCosts.insuranceIsEstimated}
                  >
                    <p>Annual insurance cost from extracted documents when available. Estimated means the app used the MVP fallback assumption because no source insurance line was found.</p>
                    <p className="m5-source-line">Source: {sourceText('insurance')}</p>
                  </BaselineMetric>
                )}
                {dealState.underwritingMetrics?.breakEvenOccupancy != null && (
                  <BaselineMetric
                    label="Break-Even Occupancy"
                    value={hasBaselinePrice ? `${(dealState.underwritingMetrics.breakEvenOccupancy * 100).toFixed(1)}%` : 'TBD'}
                    title="Break-even occupancy"
                  >
                    <p>The occupancy level needed to cover operating expenses and debt service. TBD means price or debt service assumptions are not complete.</p>
                    <p className="m5-source-line">Source: calculated from expenses, NOI, and debt service.</p>
                  </BaselineMetric>
                )}
                {hasBaselinePrice && dealState.ownershipCosts?.totalMonthlyOwnershipCost && (
                  <BaselineMetric
                    label="Total Monthly Cost"
                    value={`${money(dealState.ownershipCosts.totalMonthlyOwnershipCost)}/mo`}
                    title="Total monthly cost"
                  >
                    <p>Monthly debt service plus monthly taxes, insurance, and operating costs from the current underwriting model.</p>
                    <p className="m5-source-line">Source: calculated from loan terms, insurance, taxes, and operating expenses.</p>
                  </BaselineMetric>
                )}
              </div>
              <div className="m5-baseline-lists">
                <div>
                  <h3>Discrepancies</h3>
                  {(baselineReview.discrepancies || []).length ? baselineReview.discrepancies.slice(0, 4).map(item => (
                    <p key={`${item.type}-${item.label}`}><b>{item.label}</b>{item.message}</p>
                  )) : <p>No major sponsor-vs-baseline gaps detected from available data.</p>}
                </div>
                <div>
                  <h3>Required Actions</h3>
                  {(baselineReview.requiredActions || []).slice(0, 5).map(item => (
                    <p key={item}>{item}</p>
                  ))}
                </div>
              </div>
              <div className="m5-reality-table-wrap">
                <div className="m5-reality-header">
                  <h3>Marketing vs Reality</h3>
                  <button
                    type="button"
                    onClick={() => downloadInvestmentMemo(dealState, baselineReview, orgProfile)}
                  >
                    Generate Investment Memo
                  </button>
                </div>
                <div className="m5-reality-table">
                  <div className="m5-reality-row m5-reality-head">
                    <span>Metric</span>
                    <span>Sponsor Claim</span>
                    <span>System Baseline</span>
                    <span>Note</span>
                  </div>
                  {(baselineReview.marketingVsReality || []).map(row => (
                    <div className={`m5-reality-row severity-${row.severity || 'low'}`} key={`${row.metric}-${row.status}`}>
                      <span>{row.metric}</span>
                      <span>{row.sponsorClaim}</span>
                      <span>{row.systemBaseline}</span>
                      <span>{row.note}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="m5-baseline-lists m5-baseline-lists-extra">
                <div>
                  <h3>Risk Flags</h3>
                  {[...(baselineReview.risks || []), ...(baselineReview.discrepancies || [])].slice(0, 6).map(item => (
                    <p key={`risk-${item.type}-${item.label}`}><b>{item.label}</b>{item.message}</p>
                  ))}
                </div>
                <div>
                  <h3>Questions for Sponsor</h3>
                  {(baselineReview.criticalQuestions || []).slice(0, 6).map(item => (
                    <p key={item}>{item}</p>
                  ))}
                </div>
              </div>
            </>
            )
          })() : (
            <p className="m5-ai-explanation">Run Module 4 underwriting first so the baseline review can compare sponsor claims against calculated results.</p>
          )}
        </section>

        {/* ── Feature 2: Score Sensitivity ── */}
        <section className="m5-sensitivity-panel">
          <div className="m5-panel-title">
            <span>📐 Score Sensitivity</span>
            <strong>Driver Impact ±10 pts</strong>
          </div>
          <p className="m5-whatif-desc">
            How much does the overall deal score shift if each factor improves or worsens by 10 points?
          </p>
          <div className="m5-sens-table">
            <div className="m5-sens-header">
              <span>Factor</span>
              <span>Weight</span>
              <span>Current</span>
              <span>+10 impact</span>
              <span>−10 impact</span>
            </div>
            {sensitivity.map(s => (
              <div key={s.key} className="m5-sens-row">
                <span className="m5-sens-label">{s.label}</span>
                <span className="m5-sens-weight">{s.weight}%</span>
                <span className="m5-sens-current">{s.current}/100</span>
                <span className="m5-sens-up">+{s.scoreImpactUp.toFixed(1)} pts</span>
                <span className="m5-sens-down">{s.scoreImpactDown.toFixed(1)} pts</span>
              </div>
            ))}
          </div>
          <p className="m5-sens-note">
            Focus improvement efforts on high-weight factors for the greatest score uplift.
          </p>
        </section>
      </div>

      <div className="module-bottom-advance">
        <div style={{ display: 'flex', gap: 12, alignItems: 'stretch', flexWrap: 'wrap' }}>
          {/* Only show renegotiation button when score is strictly below 75 */}
          {needsRenegotiation && (
            <button
              onClick={() => setShowRenegotiate(true)}
              style={{
                padding: '13px 22px', borderRadius: 10,
                border: '2px solid #f59e0b',
                background: '#fffbeb', color: '#92400e',
                fontWeight: 700, fontSize: 14, cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#fef3c7' }}
              onMouseLeave={e => { e.currentTarget.style.background = '#fffbeb' }}
            >
              🔄 Renegotiation Calculator
            </button>
          )}
          <button
            className="m5-complete-btn"
            onClick={handleComplete}
            disabled={!canAdvanceNow}
            style={{
              flex: 1,
              ...(!canAdvanceNow ? {
                opacity: 0.45,
                cursor: 'not-allowed',
                background: '#94a3b8',
                boxShadow: 'none',
              } : {}),
            }}
          >
            {canAdvanceNow
              ? 'Confirm Score & Advance to Lender Match'
              : `Score ${analysis.score}/100 — Score must be ≥ 75 to advance`}
          </button>
        </div>
      </div>

      {/* ── Calculation Log Drawer ─────────────────────────────────────────── */}
      {!logOpen && (
        <button className="elog-fab" onClick={() => setLogOpen(true)}>
          Score &amp; Offer Logic
        </button>
      )}
      <aside className={`elog-drawer${logOpen ? ' open' : ''}`} aria-label="Score and offer calculation log">
        <div className="elog-header">
          <div className="elog-header-text">
            <span>Calculation Log</span>
            <strong>Score inputs and offer pricing logic</strong>
          </div>
          <button className="elog-close" onClick={() => setLogOpen(false)} aria-label="Close log">×</button>
        </div>

        {/* Inputs Used */}
        <div className="elog-section">
          <h3>Inputs Used</h3>
          <div className="elog-field-list">
            {[
              ['Asking Price',    price > 0 ? money(price) : '—',                          'from OM / deal intake'],
              ['NOI',            noi > 0 ? money(noi) : '—',                               'underwriting metrics or extracted'],
              ['Cap Rate',       offerLogic.capRate > 0 ? percent(offerLogic.capRate) : '—', 'NOI ÷ price'],
              ['DSCR',           analysis.pricingLogic && dealState.underwritingMetrics?.dscr?.current
                                   ? `${Number(dealState.underwritingMetrics.dscr.current).toFixed(2)}x` : '—', 'annual NOI ÷ annual debt service'],
              ['Cash-on-Cash',   dealState.underwritingMetrics?.cashOnCash?.year1
                                   ? percent(dealState.underwritingMetrics.cashOnCash.year1) : '—', 'year-1 cash flow ÷ equity invested'],
              ['IRR',            dealState.underwritingMetrics?.irr?.levered
                                   ? percent(dealState.underwritingMetrics.irr.levered)
                                   : dealState.underwritingMetrics?.irr?.unlevered
                                     ? percent(dealState.underwritingMetrics.irr.unlevered) : '—', 'levered or unlevered hold-period IRR'],
              ['Occupancy',      `${Number(dealState.extractedFinancials?.revenue?.rentRoll?.occupancyRate || dealState.extractedFinancials?.marketIntelligenceDetails?.marketOccupancy || 91).toFixed(0)}%`, 'rent roll or market intelligence'],
              ['Initial AI Score', `${Number(dealState.rawDealData?.initialAIScore || 76).toFixed(0)}/100`, 'AI market assessment'],
            ].map(([label, value, hint]) => (
              <div key={label}>
                <span>{label}<small>{hint}</small></span>
                <b>{value}</b>
              </div>
            ))}
          </div>
        </div>

        {/* Score Breakdown */}
        <div className="elog-section">
          <h3>Score Breakdown</h3>
          <div className="elog-field-list calculated">
            {[
              ['Financial Strength', `${analysis.breakdown.financialStrength}/100`, 'capRate×35 + DSCR×35 + CoCr×30', 'financialStrength × 0.34 = ' + (analysis.breakdown.financialStrength * 0.34).toFixed(1) + ' pts'],
              ['Market Position',    `${analysis.breakdown.marketPosition}/100`,    'AI score×0.7 + occupancy×0.3',    'marketPosition × 0.24 = ' + (analysis.breakdown.marketPosition * 0.24).toFixed(1) + ' pts'],
              ['Value-Add Upside',   `${analysis.breakdown.valueAdd}/100`,          'IRR×55 + CoCr×30 + vacancy upside','valueAdd × 0.22 = ' + (analysis.breakdown.valueAdd * 0.22).toFixed(1) + ' pts'],
              ['Risk Level',         `${analysis.breakdown.riskLevel}/100`,         '100 − DSCR penalty − cap penalty − occupancy gap', 'riskLevel × 0.20 = ' + (analysis.breakdown.riskLevel * 0.20).toFixed(1) + ' pts'],
            ].map(([label, value, hint, formula]) => (
              <div key={label}>
                <span>{label}<small>{hint}</small><p className="elog-formula">{formula}</p></span>
                <b>{value}</b>
              </div>
            ))}
            <div style={{ borderColor: '#6366f1', background: '#f5f3ff' }}>
              <span><strong>Overall Score</strong><small>weighted sum of 4 components</small></span>
              <b style={{ color: '#6366f1', fontSize: 15 }}>{analysis.score}/100</b>
            </div>
            <div>
              <span>Verdict<small>score tier</small></span>
              <b>{analysis.verdictCode}</b>
            </div>
          </div>
        </div>

        {/* Offer Pricing Logic */}
        <div className="elog-section">
          <h3>Offer Pricing Logic</h3>
          <div className="elog-field-list calculated">
            {[
              ['Risk Adjustment',    `${((1 - offerLogic.riskAdjustment) * 100).toFixed(0)}% discount`,  'score ≥85→2% · ≥72→6% · ≥60→12% · <60→20%'],
              ['NOI-Implied Value',  offerLogic.noiValue > 0 ? money(offerLogic.noiValue) : '—',         'NOI ÷ cap rate'],
              ['Recommended Offer',  money(analysis.recommended),                                          'min(price, NOI-value) × risk adjustment'],
              ['Minimum',            money(analysis.minimum),                                              'recommended − 6%'],
              ['Maximum',            money(analysis.maximum),                                              'recommended + 4%'],
              ['Discount from Ask',  price > 0 ? `${offerLogic.discountPct.toFixed(1)}%` : '—',          '(ask − recommended) ÷ ask'],
            ].map(([label, value, hint]) => (
              <div key={label}>
                <span>{label}<small>{hint}</small></span>
                <b>{value}</b>
              </div>
            ))}
          </div>
        </div>
      </aside>

    </div>
  )
}

export default Module5Page
